import React, { useState, useEffect } from 'react';
import api from '../api/api'
import Toast from '../global/toastmessage'
import { FiEye, FiEyeOff } from 'react-icons/fi';

const ProfileModal = ({ isOpen, onClose }) => {
  const [avatar, setAvatar] = useState(null); // File object
  const [previewUrl, setPreviewUrl] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [error, setError] = useState(null);
  // For displaying selected image

  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    phone: '',
    email: '',
    username: '',
    ipadId: '',
    gender: '',
    status: '',
  });

  const [toast, setToast] = useState({
    open: false,
    message: '',
    severity: 'success' 
  });

  const showToast = (message, severity = 'success') => {
    setToast({
      open: true,
      message,
      severity
    });
  };

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [passwordErrors, setPasswordErrors] = useState({
    newPassword: '',
    confirmPassword: '',
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [isSaving, setIsSaving] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleCloseToast = () => {
    setToast(prev => ({ ...prev, open: false }));
  };

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const isValidFileType = file.type.startsWith('image/');
      if (!isValidFileType) {
        showToast('Please select a valid image file', 'error');
        return;
      }
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
    }
  };
  
  const fileToBinaryString = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const uploadAvatar = async (avatarFile) => {
    if (!avatarFile) {
      showToast("Please select an image file to upload.", "error");
      return false;
    }
  
    const token = sessionStorage.getItem("accessToken");
    const formData = new FormData();
    formData.append("file", avatarFile); 
  
    try {
      const response = await api.put(`/v1/users/upload-profile-picture`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
        responseType: 'blob', 
      });
      return true
    } catch (error) {
      console.error("Upload failed", error);
      showToast("Error uploading profile picture", "error");
      return false;
    }
  };
  

  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);
  useEffect(() => {
    if (isOpen) {
      fetchUserData();
      fetchProfilePicture()
    }
  }, [isOpen]);

  const fetchUserData = async () => {
    try {
      const userid = sessionStorage.getItem('userid');
      if (!userid) {
        console.error('User ID not found in sessionStorage');
        return;
      }

      const response = await api.post(`/v1/users/${userid}`, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      });

      if (response.data) {
        setFormData({
          firstname: response.data.firstname || '',
          lastname: response.data.lastname || '',
          phone: response.data.phone || '',
          email: response.data.email || '',
          username: response.data.username || '',
          ipadId: response.data.ipadid || '',
          gender: response.data.gender || 'Male',
          status: response.data.status || 'Active',
        });
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
    }
  };

  const fetchProfilePicture = async () => {
    try {
      const token = sessionStorage.getItem("accessToken");
      const timestamp = new Date().getTime();

      const response = await api.get('/v1/users/profile-picture?t=${timestamp}', {
        responseType: 'blob', 
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const imgUrl = URL.createObjectURL(response);
      setImageUrl(imgUrl);
    } catch (error) {
      console.error('Error fetching profile picture:', error);
    }
  };

  if (error) {
    return <div className="error">Error: {error}</div>;
  }
  
  const handleSave = async () => {
    try {
      setIsSaving(true);
      const userid = sessionStorage.getItem('userid');
      if (!userid) {
        console.error('User ID not found in sessionStorage');
        return;
      }
  
      const userData = {
        firstname: formData.firstname,
        lastname: formData.lastname,
        username: formData.username,
        phone: formData.phone,
        email: formData.email,
        ipadid: formData.ipadId,
        gender: formData.gender
      };
  
      if (avatarFile) {
        const uploaded = await uploadAvatar(avatarFile);
        if (!uploaded) return;
      }
  
      const response = await api.put(`/v1/users/${userid}`, userData, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          'Content-Type': 'application/json'
        }
      });
  
      if (response.status === 200) {
        showToast(response.data?.message || 'User Updated Successfully');
        setTimeout(() => {
          onClose();
        }, 3500);
      }
    } catch (error) {
      console.error('Error updating user data:', error);
      showToast(error.response?.data?.message || 'Failed to update profile. Please try again');
    } finally {
      setIsSaving(false);
    }
  };
  
  const validatePasswordChange = () => {
    const errors = {
      newPassword: '',
      confirmPassword: ''
    };

    if (passwordData.newPassword.length < 6) {
      errors.newPassword = 'Password must be at least 6 characters';
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setPasswordErrors(errors);
    return !errors.newPassword && !errors.confirmPassword;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
  };

  const handleChangePassword = async () => {
    if (!validatePasswordChange()) {
      showToast('Please fix password errors', 'error');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const userid = sessionStorage.getItem('userid');
      if (!userid) {
        console.error('User ID not found in sessionStorage');
        return;
      }

      const payload = {
        currentpassword: passwordData.currentPassword,
        newpassword: passwordData.newPassword,
        confirmpassword: passwordData.confirmPassword
      };

      const response = await api.put(`/v1/users/${userid}/changepassword`, payload, {
        headers: {
          Authorization: `Bearer ${sessionStorage.getItem("accessToken")}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.status === 200) {
        showToast(response.data?.message || 'Password updated successfully');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        setTimeout(() => {
          onClose();
        }, 3500);
      }
      if (response.status === 201) {
        showToast(
          response.message || 'New password must be different from the current password',
          'error' 
        );
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (error) {
      console.error('Error changing password:', error);
      showToast(error.response?.data?.message || 'Failed to change password. Please try again', 'error');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 overflow-y-auto">
      <div className="bg-white w-full max-w-4xl rounded-lg shadow-lg p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-3 right-4 text-gray-500 hover:text-gray-700 text-2xl"
        >
          &times;
        </button>

        <h2 className="text-lg font-semibold mb-4 text-blue-900">User Profile</h2>
     
        <div className='overflow-y-auto max-h-[80vh] custom-scrollbar border-t pt-3'>
          <div className="col-span-2 flex flex-col items-center gap-2">
            <div
              className="w-24 h-24 rounded-full bg-gray-200 overflow-hidden cursor-pointer border-2 border-gray-300"
              onClick={() => document.getElementById("avatarInput").click()}
            >
              {imageUrl ? (
                <img src={imageUrl} alt="Profile" width={100} height={100} />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-500 text-sm">
                  Click to upload
                </div>
              )}
              <input
                id="avatarInput"
                type="file"
                accept="image/*"
                onChange={handleAvatarChange}
                className="hidden"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2">
            <div>
              <label className="block text-sm font-medium text-start">Firstname</label>
              <input
                name="firstname"
                value={formData.firstname}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Lastname</label>
              <input
                name="lastname"
                value={formData.lastname}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Phone</label>
              <input
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Email</label>
              <input
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Username</label>
              <input
                name="username"
                autoComplete='off'
                value={formData.username}
                readOnly
                className="w-full border rounded px-3 py-2 bg-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Ipad ID</label>
              <input
                name="ipadId"
                value={formData.ipadId}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Gender</label>
              <select
                name="gender"
                value={formData.gender}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              >
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-start">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full border rounded px-3 py-2"
              >
                <option>Active</option>
                <option>Inactive</option>
              </select>
            </div>
          </div>
          
          <div className='flex w-full px-2'>
            <button 
              onClick={handleSave}
              disabled={isSaving}
              className={`mt-6 px-6 py-2 bg-blue-900 text-white rounded hover:bg-blue-900 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>

          <hr className="my-6" />

          <h2 className="text-lg font-semibold mb-4 text-blue-900">Change Password</h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 px-2">
            <div className="relative">
              <label className="block text-sm font-medium text-start">Current Password</label>
              <div className="relative">
                <input
                  type={passwordVisibility.currentPassword ? "text" : "password"}
                  autoComplete='new-password'
                  name="currentPassword"
                  value={passwordData.currentPassword}
                  onChange={handlePasswordChange}
                  className="w-full border rounded px-3 py-2 pr-10"
                  placeholder="Current Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600"
                  onClick={() => togglePasswordVisibility('currentPassword')}
                >
                  {passwordVisibility.currentPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
            </div>
            
            <div className="relative">
              <label className="block text-sm font-medium text-start">New Password</label>
              <div className="relative">
                <input
                  type={passwordVisibility.newPassword ? "text" : "password"}
                  name="newPassword"
                  autoComplete='off'
                  value={passwordData.newPassword}
                  onChange={handlePasswordChange}
                  className={`w-full border rounded px-3 py-2 pr-10 ${passwordErrors.newPassword ? 'border-red-500' : ''}`}
                  placeholder="New Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600"
                  onClick={() => togglePasswordVisibility('newPassword')}
                >
                  {passwordVisibility.newPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="text-red-500 text-xs mt-1">{passwordErrors.newPassword}</p>
              )}
            </div>
            
            <div className="relative">
              <label className="block text-sm font-medium text-start">Confirm Password</label>
              <div className="relative">
                <input
                  type={passwordVisibility.confirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  autoComplete='off'
                  value={passwordData.confirmPassword}
                  onChange={handlePasswordChange}
                  className={`w-full border rounded px-3 py-2 pr-10 ${passwordErrors.confirmPassword ? 'border-red-500' : ''}`}
                  placeholder="Confirm Password"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-600"
                  onClick={() => togglePasswordVisibility('confirmPassword')}
                >
                  {passwordVisibility.confirmPassword ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="text-red-500 text-xs mt-1">{passwordErrors.confirmPassword}</p>
              )}
            </div>
          </div>
          
          <div className='flex w-full'>
            <button
              onClick={handleChangePassword}
              disabled={isUpdatingPassword}
              className={`mt-6 px-6 py-2 bg-blue-900 text-white rounded hover:bg-blue-900 ${isUpdatingPassword ? 'opacity-50 cursor-not-allowed' : ''}`}>
              Update Password
            </button>
          </div>
        </div>
      </div>
      <Toast
        open={toast.open}
        onClose={handleCloseToast}
        severity={toast.severity}
        message={toast.message}
        autoHideDuration={6000}
      />
    </div>
  );
};

export default ProfileModal;